import {
  AsYouType,
  parsePhoneNumberFromString,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';
import { isValidEmail, suggestEmailCorrection } from './intake-email';
import {
  INTAKE_EMAIL_MAX_LENGTH,
  INTAKE_FAMILY_NAME_MAX_LENGTH,
  INTAKE_GIVEN_NAME_MAX_LENGTH,
} from './intake-copy';

export type IntakeFieldName =
  | 'givenName'
  | 'familyName'
  | 'email'
  | 'phone'
  | 'phoneCountryIso'
  | 'currentCountryCode';

export type IntakeFieldErrors = Partial<Record<IntakeFieldName, string>>;

export { isValidEmail, suggestEmailCorrection };

export function dialCodeForCountry(iso2: string | null | undefined): string {
  if (!iso2 || iso2.length !== 2) return '';
  try {
    return `+${getCountryCallingCode(iso2.toUpperCase() as CountryCode)}`;
  } catch {
    return '';
  }
}

/** Format a national number as the user types, using the selected calling-code ISO. */
export function formatNationalPhone(raw: string, iso2: string): string {
  const only = raw.replace(/\D/g, '');
  if (!only) return '';
  if (!iso2 || iso2.length !== 2) return only;
  try {
    return new AsYouType(iso2.toUpperCase() as CountryCode).input(only);
  } catch {
    return only;
  }
}

/** Parse national number + ISO calling-code country into E.164, or null if invalid. */
export function toE164Phone(national: string, iso2: string): string | null {
  const trimmed = national.trim();
  const iso = iso2.trim().toUpperCase();
  if (!trimmed || iso.length !== 2) return null;
  const parsed = parsePhoneNumberFromString(trimmed, iso as CountryCode);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.format('E.164');
}

type IntakeRequiredInput = {
  givenName: string;
  familyName: string;
  noFamilyName: boolean;
  email: string;
  phone: string;
  phoneCountryIso: string;
  currentCountryCode: string;
};

export function validateIntakeFields(input: IntakeRequiredInput): {
  errors: IntakeFieldErrors;
  e164Phone: string | null;
  emailSuggestion: string | null;
} {
  const errors: IntakeFieldErrors = {};
  let emailSuggestion: string | null = null;

  if (!input.givenName.trim()) {
    errors.givenName = 'First name is required';
  } else if (input.givenName.trim().length > INTAKE_GIVEN_NAME_MAX_LENGTH) {
    errors.givenName = `First name must be ${INTAKE_GIVEN_NAME_MAX_LENGTH} characters or fewer`;
  }

  if (!input.noFamilyName && !input.familyName.trim()) {
    errors.familyName = 'Last name is required';
  } else if (
    !input.noFamilyName &&
    input.familyName.trim().length > INTAKE_FAMILY_NAME_MAX_LENGTH
  ) {
    errors.familyName = `Last name must be ${INTAKE_FAMILY_NAME_MAX_LENGTH} characters or fewer`;
  }

  if (!input.email.trim()) {
    errors.email = 'Email address is required';
  } else if (input.email.trim().length > INTAKE_EMAIL_MAX_LENGTH) {
    errors.email = `Email address must be ${INTAKE_EMAIL_MAX_LENGTH} characters or fewer`;
  } else if (!isValidEmail(input.email)) {
    errors.email = 'Enter a valid email address';
    emailSuggestion = suggestEmailCorrection(input.email);
    if (emailSuggestion) {
      const suggestedDomain = emailSuggestion.split('@')[1] ?? emailSuggestion;
      errors.email = `Check the spelling after the @ — did you mean ${suggestedDomain}?`;
    }
  } else {
    // Valid email with a likely typo — offer correction but do not block submit.
    emailSuggestion = suggestEmailCorrection(input.email);
  }

  if (!input.phoneCountryIso.trim()) {
    errors.phoneCountryIso = 'Country code is required';
  } else if (!/^[A-Za-z]{2}$/.test(input.phoneCountryIso.trim())) {
    errors.phoneCountryIso = 'Select a valid country code';
  }

  const e164Phone = toE164Phone(input.phone, input.phoneCountryIso);
  if (!input.phone.replace(/\D/g, '')) {
    errors.phone = 'Phone number is required';
  } else if (!e164Phone) {
    errors.phone = 'Enter a valid phone number';
  }

  if (!input.currentCountryCode.trim()) {
    errors.currentCountryCode = 'Country of residence is required';
  } else if (!/^[A-Za-z]{2}$/.test(input.currentCountryCode.trim())) {
    errors.currentCountryCode = 'Select a valid country';
  }

  return { errors, e164Phone, emailSuggestion };
}
